<template>
  <div class="c-report-users-card__container">
    <div class="relative">
      <BittsAvatar
        size="medium"
        :user="user || undefined"
        :show-initials="true"
      />
      <BittsAvatar
        v-if="org"
        size="x-small"
        :org="org"
        :show-initials="true"
        class="absolute -bottom-4 -right-4 border-2 border-white rounded-full"
      />
    </div>
    <div class="flex flex-col flex-1">
      <p class="text-neutral-text-strong font-bold mb-4">
        {{ user?.first_name }} {{ user?.last_name }} {{ selfIdentifier }}
      </p>
      <p class="text-neutral-text text-sm">
        {{ user?.email }}
      </p>
    </div>
    <component
      :is="showTooltip ? BittsTooltip : 'div'"
      trigger="hover"
      mount-to-body
    >
      <ReportRolePicker
        v-if="editable && !loading"
        v-model="selectedAccessLevel"
        :eligible-permissions="selectablePermissionsForPicker"
        :disabled="showTooltip"
        :use-disabled-options="true"
        :dropdown-match-select-width="false"
        class="ml-auto w-120 flex"
        data-testid="user-access-level-dropdown"
        @update:model-value="onRolePickerChange"
      />
      <p v-else-if="!loading" class="ml-auto mr-18">
        {{ capitalizedUserAccessLevel }}
      </p>
      <p v-else class="ml-auto mr-18">
        Saving
        <span class="dot1">.</span>
        <span class="dot2">.</span>
        <span class="dot3">.</span>
      </p>
      <template #title>
        {{
          isUserAdmin
            ? "Admin permissions can't be changed."
            : "You can't change your own permissions."
        }}</template
      >
    </component>
  </div>
</template>
<script setup lang="ts">
import { BittsAvatar, BittsTooltip } from '@crossbeam/bitts';
import { Nullable } from '@crossbeam/types';

import { capitalize } from 'lodash';
import { computed, ref, watch } from 'vue';

import ReportRolePicker from '@/components/reports/ReportRolePicker.vue';

import useAuth from '@/composables/useAuth';
import { useRolesStore, useTeamStore } from '@/stores';
import {
  ReportAccessLevel,
  UserWithReportAccess,
} from '@/types/report_team_access';

type OrgInfo = {
  name?: string;
  logo_url?: string | null;
  clearbit_domain?: string | null;
  domain?: string | null;
};

const {
  loading = false,
  user = null,
  isMe = false,
  editable = false,
  isCreator = false,
  eligiblePermissions = ['manager', 'editor', 'commenter', 'viewer'],
  org = null,
  reportOrganizationId = null,
} = defineProps<{
  loading?: boolean;
  user?: Nullable<UserWithReportAccess>;
  isMe: boolean;
  editable: boolean;
  isCreator: boolean;
  eligiblePermissions?: string[];
  org?: Nullable<OrgInfo>;
  reportOrganizationId?: Nullable<number>;
}>();

const emit = defineEmits<(e: 'option-selected', option: Option) => void>();

const { currentUser } = useAuth();

const showTooltip = computed(() => {
  return isUserAdmin.value || user?.id === currentUser.value?.id;
});

export type Option = {
  active?: boolean;
  danger?: boolean;
  display: string;
  description?: string;
  value: string;
  user: UserWithReportAccess;
};

const ACCESS_LEVEL_MANAGER = 'manager';
const REMOVE_USER_VALUE = 'remove_user';

const teamStore = useTeamStore();
const rolesStore = useRolesStore();

const isUserAdmin = computed(() => {
  if (!user?.id) return false;

  // External users (not from report owner's org) don't get admin elevation
  if (
    reportOrganizationId &&
    user.organization_id &&
    user.organization_id !== reportOrganizationId
  ) {
    return false;
  }

  const authorization = teamStore.getAuthorizationByUserId(user.id);
  if (!authorization?.role_id) return false;
  const role = rolesStore.getRoleById(authorization.role_id);
  return role?.name === 'Admin';
});

const selectedAccessLevel = ref<
  ReportAccessLevel | typeof REMOVE_USER_VALUE | null
>(null);

const capitalizedUserAccessLevel = computed(() => {
  if (isCreator) {
    return 'Owner';
  }
  return capitalize(user?.access_level || '');
});

const selfIdentifier = computed(() => (isMe ? '(you)' : ''));

const selectablePermissionsForPicker = computed<ReportAccessLevel[]>(() => {
  if (isUserAdmin.value) {
    return ['manager'];
  }
  return (eligiblePermissions as ReportAccessLevel[]) ?? [];
});

watch(
  [() => user?.access_level, isUserAdmin],
  ([newLevel]) => {
    if (isUserAdmin.value) {
      selectedAccessLevel.value = ACCESS_LEVEL_MANAGER;
    } else {
      selectedAccessLevel.value = (newLevel as ReportAccessLevel) || null;
    }
  },
  { immediate: true },
);

function getDisplayForValue(value: string): string {
  if (value === REMOVE_USER_VALUE) return 'Remove';
  return capitalize(value);
}

function onRolePickerChange(
  value: ReportAccessLevel | typeof REMOVE_USER_VALUE | null,
) {
  if (value == null || !user) return;

  const option: Option = {
    value,
    display: getDisplayForValue(value),
    user,
  };

  emit('option-selected', option);
}
</script>
<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-report-users-card__container {
  @apply bg-neutral-background-weak flex p-8 items-center rounded-8 mb-8 gap-6;
}
</style>
<style scoped lang="pcss">
@reference "../../css/theme.css";
.dot1 {
  animation: visibility 3s linear infinite;
}
@keyframes visibility {
  0% {
    opacity: 1;
  }
  65% {
    opacity: 1;
  }
  66% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

.dot2 {
  animation: visibility2 3s linear infinite;
}
@keyframes visibility2 {
  0% {
    opacity: 0;
  }
  21% {
    opacity: 0;
  }
  22% {
    opacity: 1;
  }
  65% {
    opacity: 1;
  }
  66% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

.dot3 {
  animation: visibility3 3s linear infinite;
}
@keyframes visibility3 {
  0% {
    opacity: 0;
  }
  43% {
    opacity: 0;
  }
  44% {
    opacity: 1;
  }
  65% {
    opacity: 1;
  }
  66% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}
</style>
